import React, { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { dashboardApi } from '@/api/dashboard.api';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { TicketTable } from '@/components/tickets/TicketTable';
import { useToasts } from '@/context/ToastContext';
import { useRealtime } from '@/context/RealtimeContext';

export function ApprovalsPage() {
  const queryClient = useQueryClient();
  const { push } = useToasts();
  const { on } = useRealtime();
  const approvals = useQuery({
    queryKey: ['dashboard', 'approvals'],
    queryFn: () => dashboardApi.approvals()
  });

  // V4.9.5 — Live updates so an admin viewing the Approvals queue sees
  // new rows appear (or existing rows disappear) the instant a solver
  // submits / a status change happens / an approval is decided.
  useEffect(() => {
    const invalidate = () => {
      void queryClient.invalidateQueries({ queryKey: ['dashboard', 'approvals'] });
    };
    const off1 = on('ticket.created', invalidate);
    const off2 = on('ticket.status_changed', invalidate);
    const off3 = on('ticket.updated', invalidate);
    const off4 = on('ticket.approval_decided', invalidate);
    const off5 = on('ticket.approval_pending', invalidate);
    return () => {
      off1();
      off2();
      off3();
      off4();
      off5();
    };
  }, [on, queryClient]);

  // V4.9.2 — Refresh = hard window reload.
  const handleRefresh = () => window.location.reload();

  return (
    <div>
      <div className="section-header">
        <h2>
          Approvals queue
          {approvals.data ? (
            <span className="text-muted text-small"> · {approvals.data.length}</span>
          ) : null}
        </h2>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={handleRefresh}
          disabled={approvals.isFetching}
        >
          {approvals.isFetching ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
      <div className="card">
        <div className="card-body no-pad">
          {approvals.isLoading ? (
            <LoadingState label="Loading approvals..." />
          ) : approvals.isError ? (
            <ErrorState error={approvals.error} onRetry={() => approvals.refetch()} />
          ) : (
            <TicketTable
              tickets={approvals.data ?? []}
              emptyLabel="No tickets are currently awaiting approval."
            />
          )}
        </div>
      </div>
    </div>
  );
}
